import { FC, useMemo, useState } from "react";
import { ScrollView } from "react-native";

import {
  MultipleChoiceQuestionResults,
  OpenEndedQuestionResults,
  PricingResult,
  QuestionHeader,
  SingleChoiceQuestionResults,
  SingleSelectMatrixQuestionResults,
  Summary,
  VerbatimsPreview,
} from "@/components/questions/results";
import { CellTabBar } from "@/components/runs";
import { StudyMenu } from "@/components/studies";
import {
  Box,
  Card,
  HStack,
  Media,
  MediaGallery,
  VStack,
} from "@/components/ui";
import { QuestionResults } from "@/types/resources";

import { AUDIENCES } from "./model";
import {
  cellLabels,
  configuredCell,
  summarize,
  surveyBlocks,
  surveyResults,
} from "./survey";
import { AudienceId, Configuration, Filters } from "./types";

/* ── Types ─────────────────────────────────────── */

interface SurveyResultsTabProps {
  audience: AudienceId;
  configuration: Configuration;
  filters: Filters;
}

/* ── Component ─────────────────────────────────── */

/**
 * Question-by-question results of the simulated survey, laid out like a study
 * run: question header, AI summary, distribution chart and the platform's own
 * navigation menu. Groups are the five segment audiences; the toolbar audience
 * picks one (or all of them), and the demographic filters narrow every group.
 */
const SurveyResultsTab: FC<SurveyResultsTabProps> = ({
  audience,
  configuration,
  filters,
}) => {
  const blocks = useMemo(() => surveyBlocks(), []);
  const groupedData = useMemo(
    () => blocks.map((b) => ({ data: b.questions, section: b.section })),
    [blocks]
  );
  const [activeId, setActiveId] = useState(blocks[0].questions[0].id);
  const [cellOverride, setCellOverride] = useState<Record<string, number>>({});
  const [galleryOpen, setGalleryOpen] = useState(false);

  const block = blocks.find((b) => b.questions.some((q) => q.id === activeId));
  const question = block?.questions.find((q) => q.id === activeId);
  const section = block?.section ?? null;
  const selectedCell = section
    ? (cellOverride[section.id] ?? configuredCell(section, configuration))
    : null;

  const result = useMemo(
    () =>
      question
        ? surveyResults(question, section, configuration, filters)
        : null,
    [configuration, filters, question, section]
  );

  const selectedGroupIndex = audience === "all" ? "all" : audience;

  // Narrow to one cell for rotated sections. Choice charts compare the five
  // segments when "All US consumers" is selected; the insights, verbatims and
  // the open-ended sentiment chart come from the nationally representative
  // group so they match the Results tab.
  const { narrowed, answers } = useMemo(() => {
    if (!result) return { answers: [], narrowed: null };
    const byCell = result.groups.map((g) => ({
      ...g,
      answers:
        selectedCell === null
          ? g.answers
          : g.answers.filter((a) => a.cell === selectedCell),
    }));
    const focus = byCell.filter((g) => g.group.id === audience);
    const compared =
      audience === "all" && result.question.type !== "open_ended"
        ? byCell.filter((g) => g.group.id !== "all")
        : focus;
    return {
      answers: focus.flatMap((g) => g.answers),
      narrowed: { ...result, groups: compared } as QuestionResults,
    };
  }, [audience, result, selectedCell]);

  const summary = useMemo(() => {
    if (!question) return null;
    const scope =
      audience === "all"
        ? "US consumers"
        : (AUDIENCES.find((a) => a.id === audience)?.label ?? "US consumers");
    return summarize(question, answers, scope, configuration);
  }, [answers, audience, configuration, question]);

  // Result components read charts off the summaries; the verbatims preview
  // ranks answers by text, so hand it one answer per distinct quote.
  const chartResult = useMemo<QuestionResults | null>(
    () =>
      narrowed
        ? {
            ...narrowed,
            groups: narrowed.groups.map((g) => ({ ...g, summary })),
            summary,
          }
        : null,
    [narrowed, summary]
  );
  const distinctAnswers = useMemo(() => {
    const seen = new Set<string>();
    return answers.filter(({ answer }) => {
      const text = answer.text ?? "";
      if (seen.has(text)) return false;
      seen.add(text);
      return true;
    });
  }, [answers]);

  const onMenuPress = (id: string) => {
    if (id.startsWith("section-")) {
      const target = blocks.find(
        (b) => b.section?.id === id.slice("section-".length)
      );
      if (target) setActiveId(target.questions[0].id);
      return;
    }
    setActiveId(id);
  };

  if (!question || !chartResult) return null;

  const cell =
    section && selectedCell !== null ? section.cells[selectedCell] : null;
  // The packshot is rendered here, borderless, rather than by QuestionHeader.
  const variables = section?.config?.variables ?? [];
  const textVariables = variables.filter((v) => v.type !== "image");
  const imageUri = variables
    .filter((v) => v.type === "image")
    .map((v) => cell?.[`${v.name}_url`])
    .find((uri): uri is string => typeof uri === "string");

  return (
    <HStack className="flex-1 min-h-0 flex-col lg:flex-row gap-4 items-stretch">
      <ScrollView
        className="flex-1 min-w-0"
        showsVerticalScrollIndicator={false}
      >
        <VStack className="gap-4 pb-8">
          <Card className="border border-outline-200 rounded-2xl p-5">
            <VStack className="gap-3">
              <QuestionHeader
                cell={cell}
                question={question}
                sectionTitle={section?.title}
                variables={textVariables}
              />
              {imageUri && (
                <>
                  <Media
                    alt={question.text}
                    className="border-0 border-transparent"
                    height={96}
                    // Dev asset URIs differ only by query string; force a remount per cell.
                    key={imageUri}
                    onPress={() => setGalleryOpen(true)}
                    resizeMode="contain"
                    source={imageUri}
                    width={96}
                  />
                  <MediaGallery
                    media={[imageUri]}
                    onClose={() => setGalleryOpen(false)}
                    visible={galleryOpen}
                  />
                </>
              )}
              {section && selectedCell !== null && (
                <CellTabBar
                  labels={cellLabels(section)}
                  onChange={(index) =>
                    setCellOverride((prev) => ({
                      ...prev,
                      [section.id]: index,
                    }))
                  }
                  selected={selectedCell}
                />
              )}
            </VStack>
          </Card>

          <VStack className="gap-6">
            <Summary summary={summary} />
            {renderChart(question.type, chartResult, selectedGroupIndex)}
            {question.type === "open_ended" && (
              <VerbatimsPreview
                answers={distinctAnswers}
                questionId={question.id}
                runId={null}
                selectedGroupIndex={selectedGroupIndex}
              />
            )}
          </VStack>
        </VStack>
      </ScrollView>

      <Box className="w-full lg:w-[320px] lg:shrink-0 lg:h-full">
        <StudyMenu
          activeSectionId={activeId}
          groupedData={groupedData}
          loading={false}
          onItemPress={onMenuPress}
        />
      </Box>
    </HStack>
  );
};

export default SurveyResultsTab;

/* ── Private ────────────────────────────────────── */

const renderChart = (
  type: string,
  result: QuestionResults,
  selectedGroupIndex: "all" | string
) => {
  switch (type) {
    case "single_choice":
      return (
        <SingleChoiceQuestionResults
          result={result}
          selectedGroupIndex={selectedGroupIndex}
        />
      );
    case "multiple_choice":
      return (
        <MultipleChoiceQuestionResults
          result={result}
          selectedGroupIndex={selectedGroupIndex}
        />
      );
    case "pricing":
      return <PricingResult result={result} />;
    case "single_select_matrix":
      return <SingleSelectMatrixQuestionResults result={result} />;
    case "open_ended":
      return <OpenEndedQuestionResults result={result} />;
    default:
      return null;
  }
};
